import type { ReactElement, ReactNode } from 'react';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Notice } from '@/components/Notice';
import { Skeleton } from '@/components/Skeleton';
import { SETTLED } from '@/lib/density';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { routeErrorMessage } from '@/lib/routeErrorMessage';
import { signInHref } from '@/lib/session';

/**
 * `FE-13.5` (`FR-EXP-034`) — the board face's four states, and the whole
 * point of this file is that they are told apart by **role and accessible
 * name**, not merely by the words on screen: `BoardLoading` is
 * `role="status"` named "Loading the board"; `BoardError` is `role="alert"`
 * (via `EmptyState tone='error'`) plus a `Try again` button; `BoardEmpty`
 * and `ListEmpty` carry no live-region role at all — a heading is the
 * announcement, per `EmptyState`'s own contract; `BoardClosedNotice` is
 * `role="status"` again, but named after the board rather than "Loading…",
 * so a screen reader user does not have to fall back on reading the body
 * text to tell "still loading" from "closed" apart.
 *
 * `BoardEmpty`'s `Add a list` stays the plain, inert fallback button C1
 * shipped unless a caller passes `action` (`FE-11.1`, chunk `C2`, wires the
 * real `ListComposer` there). `ListEmpty`'s `Add a card to <list>` button is
 * wired more directly: `onAddCard` (`FE-12.1`, chunk `C3`) is an optional
 * callback this file calls straight from the button's `onClick` — `ListColumn`
 * is the only caller that passes one, and it points at the SAME bottom
 * composer its own non-empty-list button opens (`C3-BRIEF.md` §6.5). This
 * file's own tests below render `ListEmpty` without it and see the inert
 * button, unchanged from before C3.
 */

/** Decorative column/card counts for the loading skeleton. Not the density
 * budget's numbers (`lib/density.ts`'s `SETTLED`/`PROVISIONAL`) — those
 * price a *real* board at a *real* viewport; this is just enough shape to
 * read as "a board is loading" rather than "a grey rectangle". */
const LOADING_COLUMN_COUNT = 3;
const LOADING_CARD_COUNT = 3;

/**
 * The skeleton strip — `FR-EXP-034` AC4 ("a loading state is shown … never
 * left ambiguous") and its own distinguishability requirement.
 *
 * Shaped like columns (a title bar plus a few card-height rows each), not a
 * single block, so it is recognisable as "a board loading" by eye. The
 * accessible name comes from an explicit `aria-label` rather than from the
 * (aria-hidden) skeleton content, so it holds regardless of how a given
 * accessible-name algorithm treats `role="status"` content — the one thing
 * this component must never get wrong is a screen reader user hearing
 * nothing, or hearing the same name loading and empty both carry.
 */
export function BoardLoading(): ReactElement {
  const { t } = useInterfaceLanguage();

  return (
    <div
      role="status"
      aria-label={t('board.loading')}
      aria-busy="true"
      className="flex h-full gap-3 overflow-hidden p-3"
    >
      {Array.from({ length: LOADING_COLUMN_COUNT }).map((_, index) => (
        <div
          key={index}
          aria-hidden="true"
          className="flex shrink-0 flex-col gap-3 rounded-card bg-panel p-2"
          style={{ width: SETTLED.columnWidth }}
        >
          <Skeleton shape="line" width="60%" />
          <Skeleton shape="row" count={LOADING_CARD_COUNT} />
        </div>
      ))}
    </div>
  );
}

export interface BoardErrorProps {
  error: unknown;
  onRetry: () => void;
}

/**
 * The failed-load state — `FR-EXP-034`'s second edge case: an empty state
 * caused by a failed load must say so and offer retry, never claim the
 * surface is genuinely empty. `EmptyState tone='error'` is the primitive
 * that draws that line (`role="alert"`, never defaulted); `routeErrorMessage`
 * is the same translation the route-level error boundary already uses to
 * turn `error` into a sentence a member can act on, so a render bug or a
 * third-party throw never surfaces a stack or an error code here either.
 */
export function BoardError({ error, onRetry }: BoardErrorProps): ReactElement {
  const { t } = useInterfaceLanguage();

  return (
    <EmptyState
      tone="error"
      size="panel"
      title={t('board.error.title')}
      body={routeErrorMessage(error, t('route.error.genericBody'))}
      action={
        <Button variant="primary" onClick={onRetry}>
          {t('board.error.retry')}
        </Button>
      }
    />
  );
}

export interface BoardEmptyProps {
  boardTitle: string;
  canEdit: boolean;
  /**
   * `FE-11.1` (chunk `C2`) — the wired `ListComposer` (`trigger='button'`)
   * a caller now has to pass so an empty, editable board opens the real
   * composer rather than an inert button. Optional and additive on
   * purpose: a caller that does not yet pass one (this file's own tests
   * included) keeps getting the plain `Add a list` button C1 shipped, so
   * those tests keep meaning what they meant. Rendered in `EmptyState`'s
   * `action` slot, and only when `canEdit` — same gate as the fallback
   * button it replaces.
   */
  action?: ReactNode;
}

/**
 * A board with no lists — `FR-EXP-034` AC1: "an empty state names the board
 * and offers to add the first list." The explanation (title + body) always
 * renders; the action renders only when `canEdit`, so a read-only member
 * sees why the board looks empty without a button they cannot use (the
 * first edge case). `canEdit` is computed once, in `lib/board-face.ts`
 * (agent A) — this component only gates on it, per `C1-BRIEF.md` §3.
 */
export function BoardEmpty({
  boardTitle,
  canEdit,
  action,
}: BoardEmptyProps): ReactElement {
  /*
   * FE-80.1 — routed with `ListComposer.tsx`, and the fallback button is the
   * reason the two files are one slice. It renders the SAME copy for the SAME
   * affordance as the composer's trigger (see `BoardEmptyProps.action`), so it
   * asks for the SAME key: `boardFace.list.add`, declared once, out of the
   * `board.*` family this file otherwise uses. A `board.empty.action` declared
   * beside the other two would translate independently of the composer, and
   * the empty board would then disagree with the full one in every language
   * but the one nobody would test in.
   */
  const { t } = useInterfaceLanguage();
  return (
    <EmptyState
      tone="neutral"
      size="panel"
      title={t('board.empty.title', { title: boardTitle })}
      body={t('board.empty.body')}
      action={
        canEdit
          ? (action ?? <Button variant="primary">{t('boardFace.list.add')}</Button>)
          : undefined
      }
    />
  );
}

export interface ListEmptyProps {
  listTitle: string;
  canEdit: boolean;
  /**
   * `FE-12.1` (chunk `C3`) — opens the bottom card composer, the SAME one
   * the non-empty column's own "Add a card" button opens (`C3-BRIEF.md`
   * §6.5: "wire it to the same callback, do not add a third"). Optional and
   * additive: a caller that does not pass it (this file's own tests
   * included) keeps the plain, inert button C1 shipped.
   */
  onAddCard?: (() => void) | undefined;
}

/**
 * A list with no cards. Same `canEdit` rule as `BoardEmpty`: the
 * explanation always renders, the action only when `canEdit`.
 *
 * ⚠ **`headingLevel={3}`, and it is not cosmetic.** This renders inside
 * `ListColumn`, whose list name is already an `<h2>`; `EmptyState`'s default
 * rank of 2 therefore nested two rank-2 headings in one section — the list
 * named twice, at the same level, with nothing saying the second is
 * subordinate. Two C1 agents reported it independently and neither one's tests
 * could see it, because each file was correct alone. `EmptyState` gained the
 * prop for this; see its docstring.
 */
export function ListEmpty({
  listTitle,
  canEdit,
  onAddCard,
}: ListEmptyProps): ReactElement {
  /*
   * FE-80.1 — the same split `BoardEmpty` above records, one level down. The
   * title and body are this file's own sentences and stay in `board.*`; the
   * BUTTON is shared vocabulary and reaches for the keys `ListColumn.tsx`
   * already routes for the identical affordance on a non-empty list
   * (`:642`/`:645`) — one list, two mutually-exclusive renderings of one
   * control (`C3-BRIEF.md` §6.5). Declaring `board.listEmpty.action` beside the
   * other two would let the empty list and the full list disagree in every
   * language but this one.
   */
  const { t } = useInterfaceLanguage();

  return (
    <EmptyState
      tone="neutral"
      /*
       * ⚠ `inline`, not `panel`, and the difference is 24px of padding per edge
       * inside a **292px** column. `panel` is `p-12`, which leaves ~180px of
       * text width and makes an empty list visibly taller and louder than a
       * full one — the reverse of what a board should emphasise, and a direct
       * hit on the product's own tie-breaker ("a board must stay readable in
       * ten seconds"). `BoardEmpty` keeps `panel`, because there the empty
       * state IS the whole surface.
       */
      size="inline"
      headingLevel={3}
      title={t('board.listEmpty.title', { list: listTitle })}
      body={t('board.listEmpty.body')}
      action={
        canEdit ? (
          /*
           * Visible label short, accessible name specific — see the matching
           * comment in `ListColumn`. A list title is board-seeded and can be
           * 40 characters; the column is 292px.
           *
           * `onAddCard` opens the SAME bottom composer `ListColumn`'s own
           * button opens for a non-empty list — `ListColumn` is the only
           * caller that wires it (`C3-BRIEF.md` §6.5); this file's own tests
           * render `ListEmpty` without it and get the plain button C1
           * shipped, unwired.
           */
          <Button
            variant="secondary"
            size="sm"
            aria-label={t('boardFace.list.addCardTo', { list: listTitle })}
            onClick={onAddCard}
          >
            {t('boardFace.list.addCard')}
          </Button>
        ) : undefined
      }
    />
  );
}

export interface BoardClosedNoticeProps {
  boardTitle: string;
  /**
   * `FR-EXP-012` AC2's Reopen action — `FE-08.3`, chunk `D3`.
   *
   * ⚠ Widened `| undefined` rather than left bare-optional: under
   * `exactOptionalPropertyTypes` a conditional JSX spread compiles against a
   * component that declares no such prop at all, which is how `C2` shipped
   * drag-handle plumbing type-checked by nothing.
   */
  onReopen?: (() => void) | undefined;
  reopenBusy?: boolean | undefined;
  /**
   * Non-null exactly when the control renders **disabled** — `D3-BRIEF.md`
   * §2.2: close and reopen are *locked, never hidden*.
   */
  reopenDisabledReason?: string | undefined;
}

/**
 * `FR-EXP-012` AC2: a closed board renders read-only with a stated reason
 * rather than a not-found page. `aria-label` gives this its own accessible
 * name (distinct from `BoardLoading`'s, though both are `role="status"`),
 * so a screen reader user does not have to read the body to tell the two
 * apart.
 *
 * **The Reopen action landed in `D3` (`FE-08.3`)** and is the `onReopen` prop.
 * C1 rendered only the read-only notice and said so here; that note is replaced
 * rather than left to go stale, because a wrong claim in the file that owns the
 * behaviour is worse than no claim (`D2`'s lesson on `routes/index.tsx`).
 *
 * ⚠ **This `Notice` is already `role="status"`.** Its appearance *is* the
 * announcement for a close or a reopen — `D3-BRIEF.md` §5.7's one-channel rule
 * — so the caller must **not** also call `announce()` for those two events, or
 * the sentence is read twice (`D2` finding 5).
 */
export function BoardClosedNotice({
  boardTitle,
  onReopen,
  reopenBusy,
  reopenDisabledReason,
}: BoardClosedNoticeProps): ReactElement {
  /*
   * FE-80.1 — routed, and NO `announce()` was added with it. This `Notice` is
   * already `role="status"` and its appearance IS the announcement
   * (`D3-BRIEF.md` §5.7's one-channel rule, `D2` finding 5); a second channel
   * would read the sentence twice.
   *
   * ⚠ `board.closed.body` keeps its slot FIRST and `aria-label` stays an
   * `aria-label`. `FR-EXP-012.spec.ts:739` and `J3.spec.ts:137` find this
   * notice by `hasText`, which reads TEXT CONTENT — so it is the BODY that
   * satisfies them, by carrying `<title> is closed` as a prefix substring —
   * while `BoardStates.test.tsx:143` asserts the exact accessible name, which
   * is the label. Two bindings, two keys, neither interchangeable.
   */
  const { t } = useInterfaceLanguage();

  return (
    <Notice
      tone="info"
      title={t('board.closed.title')}
      body={t('board.closed.body', { title: boardTitle })}
      aria-label={t('board.closed.label', { title: boardTitle })}
      /*
       * `Notice` already has an `action` slot, so nothing is widened for this
       * — the reason `BoardMenuPanel` is a new component rather than a widened
       * `Dialog` (`D3-BRIEF.md` §2.1) is that no primitive fitted; here one
       * does. A caller that passes no `onReopen` (this file's own tests
       * included, and every C1 call site before `D3`) renders exactly the
       * notice C1 shipped.
       */
      action={
        onReopen === undefined ? undefined : (
          <ReopenAction
            onReopen={onReopen}
            busy={reopenBusy === true}
            reason={reopenDisabledReason}
          />
        )
      }
    />
  );
}

interface ReopenActionProps {
  onReopen: () => void;
  busy: boolean;
  /** Defined exactly when the control renders **refused** — §2.2's lock. */
  reason: string | undefined;
}

/**
 * `FR-EXP-012` AC2's control, and its edge case's lock.
 *
 * ⚠ **Two written-out branches, not `disabledReason={reason ?? undefined}`.**
 * `Button` declares `disabledReason?: string` and `busy?: boolean`, and under
 * `exactOptionalPropertyTypes` neither accepts an explicit `undefined`; a
 * conditional JSX spread *would* compile and is the defect-ledger shape this
 * chunk forbids. `BoardMenuPanel`'s `LifecycleEntry` is written the same way
 * for the same reason.
 *
 * ⚠ **`busy` is declared on both branches** — `Button` reserves its busy slot
 * whenever the prop is present at all, which is what keeps the two states the
 * same width (`Button.tsx`), and it renders its own `sr-only role="status"`
 * for it. That nests a `role="status"` inside this `Notice`'s: a query for
 * `role="status"` on a closed board face matches **both**, so scope by the
 * notice's `aria-label` (`${title} is closed`) or by `within(notice)`.
 *
 * `variant="secondary"`, not `primary`: reopening is the *available* action on
 * a closed board, not the page's call to action — the board behind it is what
 * the member came for.
 */
function ReopenAction({ onReopen, busy, reason }: ReopenActionProps): ReactElement {
  /*
   * FE-80.1 — this file's only non-exported component still needs its own
   * `useInterfaceLanguage()`; it is a component, so the hook is legal, and it
   * must sit above the early return.
   *
   * ⚠ ONE key across BOTH branches. `Reopen board` is named exactly once
   * product-wide by design (`BoardMenuPanel.tsx:263`, `FR-EXP-012.spec.ts:713`)
   * so that `getByRole('button', { name: 'Reopen board' })` is unambiguous; a
   * second key for the disabled branch would let the lock and the offer say
   * different things in the first catalogue served.
   */
  const { t } = useInterfaceLanguage();

  if (reason !== undefined) {
    return (
      <Button variant="secondary" size="sm" busy={busy} disabled disabledReason={reason}>
        {t('board.closed.reopen')}
      </Button>
    );
  }

  return (
    <Button variant="secondary" size="sm" busy={busy} onClick={onReopen}>
      {t('board.closed.reopen')}
    </Button>
  );
}

export interface BoardSignInPromptProps {
  /** Where to return after signing in; becomes `?next=`. */
  returnPath: string;
}

/**
 * `FR-PERM-001` AC1 clause 1 — `FE-08.4`, chunk `D3`.
 *
 * "Given a signed-out visitor opens a private board URL, then they are
 * **prompted to sign in** rather than told the board does not exist."
 *
 * ---------------------------------------------------------------------------
 * ⚠ WHY THIS IS AN IN-PAGE PROMPT AND NOT A REDIRECT
 * ---------------------------------------------------------------------------
 * The seam this closes had two halves that each deferred to the other:
 * `boards.ts` answers **404, never 401** — a 401 there is the existence oracle
 * `FR-PERM-008` forbids — and `main.tsx`'s global redirect is bound on
 * `isUnauthenticated`, which no board query can produce.
 *
 * AC1's wording names no URL change, so an in-page prompt satisfies it, and it
 * is the only shape that costs nothing elsewhere: teaching the global binding
 * that "`not_found` while signed out" is a session condition would bounce a
 * signed-out visitor away from **every** future 404 in the product.
 * `D3-BRIEF.md` §2.4 records the decision; `FR-PERM-001.spec.ts`'s block
 * comment argued it first.
 *
 * ⚠ **This must never render the string "No such board".** That is the
 * falsifiable core of the criterion — a visitor must not be told that a board
 * they simply cannot see does not exist — and it is what the spec asserts. The
 * copy below therefore says nothing about whether this address names a board:
 * §5.3 — the client cannot tell "no such board" from "a board you may not see"
 * (one 404, one `code`, deliberately, so the route is not an existence
 * oracle), and **must not try**. What it *can* say is local knowledge: you are
 * signed out, and signing in is what would let this address resolve.
 *
 * ⚠ **The `next` is built by `signInHref`, never hand-spelled.** That function
 * owns the one encoding `safeNextPath` reads back, refuses to make `/sign-in`
 * its own destination, and omits the key entirely for `/` — three rules a
 * second spelling of `?next=` would have to re-derive and would get wrong the
 * first time a board title produced a character that encodes differently
 * (`routes/w.$workspaceId.members.tsx` and `RouteNotFound` build theirs the
 * same way).
 *
 * `EmptyState`, not a hand-built page: this state *replaces* the board face
 * rather than sitting above it, which is the line `Notice`'s own docstring
 * draws between the two primitives. `headingLevel` is left at its default of
 * 2, matching `BoardError` — the sibling state on this same route — rather
 * than promoting one failure state to `<h1>` and leaving the other at 2.
 */
export function BoardSignInPrompt({ returnPath }: BoardSignInPromptProps): ReactElement {
  const { t } = useInterfaceLanguage();
  /*
   * `signInHref` takes path parts, so a `returnPath` carrying a query string or
   * permalink hash is split here rather than passed whole — `/b/x/y?filter=z`
   * with the search folded into `pathname` would encode identically today and
   * is exactly the kind of second spelling the note above is about. `search`
   * and `hash` keep their leading punctuation, matching `window.location` and
   * TanStack's location object.
   */
  const hashAt = returnPath.indexOf('#');
  const beforeHash = hashAt === -1 ? returnPath : returnPath.slice(0, hashAt);
  const hash = hashAt === -1 ? '' : returnPath.slice(hashAt);
  const queryAt = beforeHash.indexOf('?');
  const pathname = queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt);
  const search = queryAt === -1 ? '' : beforeHash.slice(queryAt);

  return (
    <EmptyState
      tone="neutral"
      size="panel"
      title={t('board.signIn.title')}
      body={t('board.signIn.body')}
      action={
        <Button as="a" variant="primary" href={signInHref({ pathname, search, hash })}>
          {t('board.signIn.action')}
        </Button>
      }
    />
  );
}
